import { useMemo } from "react";

import { useAudio } from "@aircall/tractor";

import {
  getLeftPercentage,
  getWidthPercentage,
} from "./PlayerSpeakersList.utilities";

import { useCall } from "@/hooks/useCall/useCall";
import { SpeakersType } from "@/states/call/speakers/speakers.utility.decl";

export type SegmentData = {
  startTime: number;
  left: number;
  width: number;
  index: number;
};

type SpeakersSegments = {
  channel: number;
  segments: SegmentData[];
}[];

/**
 * The hook responsible for converting each utterance to the segment dimension and map it to the relevant speaker.
 */
export const useSegmentsPerSpeaker = (
  speakers: NonNullable<SpeakersType>
): SpeakersSegments => {
  const { call } = useCall();
  const { duration } = useAudio();

  // Sorting each utterance to it's channel/speaker
  return useMemo(() => {
    const segmentsPerSpeaker = new Map<number, SegmentData[]>();

    if (!call?.utterances?.length) {
      return [];
    }

    // Initializing all speakers with empty segments array

    // Initializing all speakers with empty segments array
    [...speakers.keys()].forEach((channel) =>
      segmentsPerSpeaker.set(channel, [])
    );

    call.utterances.forEach((u, index) => {
      const data = {
        startTime: u.startTime,
        left: getLeftPercentage(u.startTime, duration),
        width: getWidthPercentage(u.startTime, u.endTime, duration),
        index,
      };

      segmentsPerSpeaker.get(u.channel)?.push(data);
    });

    // Converting Map to an array for easy sorting and rendering
    return [...segmentsPerSpeaker].map(([channel, segments]) => ({
      channel,
      segments,
    }));
  }, [call?.utterances, speakers, duration]);
};

/**
 * The hook responsible for sorting the speakers in the following order:
 * 1) Agents (even if they don't have any segment).
 * 2) Clients (only if they have segments).
 * 3) Silent clients which don't have any segment.
 * @param segmentsPerSpeaker
 * @param speakers
 */
export const useSortSpeakers = (
  segmentsPerSpeaker: SpeakersSegments,
  speakers: NonNullable<SpeakersType>
) =>
  useMemo(() => {
    // Finding all the agents - should be displayed first. (even if they don't have segments)
    const agents = segmentsPerSpeaker.filter(
      ({ channel }) => speakers.get(channel)?.agentID
    );

    // Finding all the clients which have segments.
    const noneEmptyClients = segmentsPerSpeaker.filter(
      ({ channel, segments }) =>
        !speakers.get(channel)?.agentID && segments.length > 0
    );

    // Finding all the clients which don't have any segments. (they should be displayed in the end)
    const silentClients = [...segmentsPerSpeaker].filter(
      ({ channel, segments }) =>
        !speakers.get(channel)?.agentID && segments.length === 0
    );

    return [...agents, ...noneEmptyClients, ...silentClients];
  }, [speakers, segmentsPerSpeaker]);
